<template>
  <div class="container-fluid py-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-md-8">
        <div class="d-flex align-items-center">
          <div class="bg-primary-light p-3 rounded-2 me-3">
            <vue-feather type="repeat" class="d-flex justify-content-center align-content-center" stroke="#652aa3" size="24"></vue-feather>
          </div>
          <h3 class="mb-0 fw-bold">Review Cycles</h3>
        </div>
      </div>
      <div class="col-md-4">
        <div class="d-flex justify-content-end">
          <button class="btn btn-primary shadow-sm" @click="openCreate">
            <vue-feather type="plus" size="18" class="me-1"></vue-feather>
            Add Review Cycle
          </button>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <Card cardClasses="shadow-sm border-0 h-100 mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div class="font-size-125 color-gray-600">
              <vue-feather type="repeat" stroke="#652aa3" size="18" class="me-1"></vue-feather>
              Review Cycles ({{ filtered.length }})
            </div>
            <div class="d-flex gap-2 align-items-center">
              <select v-model="statusFilter" class="form-select form-select w-auto" data="">
                <option value="">All</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
              <UInput v-model="search" size="md" placeholder="Search cycles" class="mb-0 w-25 search-input"/>
            </div>
          </div>

          <!-- Spinner -->
          <div v-if="loading" class="text-center py-4">
            <Spinner spinner-class="mb-1" />
          </div>

          <!-- Table -->
          <div v-if="!loading && filtered.length" class="table-responsive">
            <table class="table table-hover align-middle">
              <thead class="table-light">
                <tr>
                  <th class="fw-semibold">Name</th>
                  <th class="fw-semibold">Dates</th>
                  <th class="fw-semibold">Deadlines</th>
                  <th class="fw-semibold">Status</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="cycle in filtered" :key="cycle.id">
                  <td class="fw-semibold">
                    <router-link :to="`/review-cycle/${cycle.id}`" class="text-decoration-none">
                      {{ cycle.name }}
                    </router-link>
                  </td>
                  <td class="text-muted">
                    {{ formatDate(cycle.start_date) }} — {{ formatDate(cycle.end_date) }}
                  </td>
                  <td class="text-muted">
                    <div v-if="cycle.self_review_deadline || cycle.manager_review_deadline" class="small">
                      <div v-if="cycle.self_review_deadline">Self: {{ formatDate(cycle.self_review_deadline) }}</div>
                      <div v-if="cycle.manager_review_deadline">Manager: {{ formatDate(cycle.manager_review_deadline) }}</div>
                    </div>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <span :class="statusBadge(cycle.status)">{{ cycle.status || 'draft' }}</span>
                  </td>
                  <td class="text-end">
                    <div class="btn-group btn-group-sm">
                      <router-link :to="`/review-cycle/${cycle.id}`" class="btn btn-outline-info" title="View">
                        <vue-feather type="eye" size="14"></vue-feather>
                      </router-link>
                      <button class="btn btn-outline-primary" title="Edit" @click="openEdit(cycle)">
                        <vue-feather type="edit" size="14"></vue-feather>
                      </button>
                      <button v-if="cycle.status !== 'active' && cycle.status !== 'closed'" class="btn btn-outline-success" title="Activate" @click="activate(cycle)">
                        <vue-feather type="zap" size="14"></vue-feather>
                      </button>
                      <button class="btn btn-outline-danger" title="Delete" @click="confirmDelete(cycle)">
                        <vue-feather type="trash" size="14"></vue-feather>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty -->
          <div v-if="!loading && !filtered.length" class="text-center py-5">
            <vue-feather type="repeat" size="48" class="mb-3" stroke="#652aa3"></vue-feather>
            <h5>No Review Cycles Found</h5>
            <p class="text-muted">Create your first review cycle to get started.</p>
            <button class="btn btn-primary" @click="openCreate">
              <vue-feather type="plus" size="16" class="me-1"></vue-feather>
              Add Review Cycle
            </button>
          </div>
        </Card>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <Modal :open="showModal" @closed="closeModal" dialog-size="modal-lg">
      <div class="modal-header border-0">
        <h5 class="modal-title m-0">{{ isEditing ? 'Edit Review Cycle' : 'Add Review Cycle' }}</h5>
        <button @click="closeModal" type="button" class="btn-close outline-none"></button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="save">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Name</label>
              <input v-model="form.name" type="text" class="form-control" required maxlength="255" placeholder="Enter cycle name" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Status</label>
              <select v-model="form.status" class="form-select" data="">
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div class="col-12">
              <label class="form-label">Description</label>
              <textarea v-model="form.description" class="form-control" rows="2" placeholder="Short description (optional)"></textarea>
            </div>
            <div class="col-md-6">
              <label class="form-label">Review Template</label>
              <select v-model="form.review_template_id" class="form-select" required>
                <option value="" disabled>Select a template</option>
                <option v-for="template in templates" :key="template.id" :value="template.id">
                  {{ template.name }}
                </option>
              </select>
              <div v-if="loadingTemplates" class="small text-muted mt-1">Loading templates...</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Start Date</label>
              <input v-model="form.start_date" type="date" class="form-control" required placeholder="YYYY-MM-DD" />
            </div>
            <div class="col-md-6">
              <label class="form-label">End Date</label>
              <input v-model="form.end_date" type="date" class="form-control" required placeholder="YYYY-MM-DD" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Self Review Deadline</label>
              <input v-model="form.self_review_deadline" type="date" class="form-control" placeholder="YYYY-MM-DD (optional)" />
            </div>
            <div class="col-md-6">
              <label class="form-label">Manager Review Deadline</label>
              <input v-model="form.manager_review_deadline" type="date" class="form-control" placeholder="YYYY-MM-DD (optional)" />
            </div>
            <div class="col-md-12">
              <label class="form-label">Settings (JSON, optional)</label>
              <textarea v-model="settingsJson" class="form-control" rows="3" placeholder='{"allow_self_edit":true}'></textarea>
            </div>
          </div>
          <div class="d-flex justify-content-end mt-4">
            <button type="button" class="btn btn-outline-secondary me-2" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              {{ isEditing ? 'Update' : 'Save' }}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import moment from 'moment'
import notify from '@/utils/notify'
import confirmDialog from '@/utils/confirmDialog'
import reviewCycleService from '@/api/reviewCycleService'
import reviewTemplateService from '@/api/reviewTemplateService'

export default {
  name: 'ReviewCycles',
  components: { VueFeather },
/* <<<<<<<<<<<<<<  ✨ Windsurf Command ⭐ >>>>>>>>>>>>>>>> */
  /**
   * The component's data.
   * @property {Boolean} loading - Whether the component is currently loading data.
   * @property {Boolean} saving - Whether the component is currently saving data.
   * @property {Array<Object>} items - The list of review cycles.
   * @property {String} search - The search query for filtering review cycles.
   * @property {String} statusFilter - The status to filter review cycles by.
   * @property {Boolean} showModal - Whether the create/edit modal is currently open.
   * @property {Boolean} isEditing - Whether the create/edit modal is currently in edit mode.
   * @property {Number} editingId - The ID of the review cycle being edited.
   * @property {Array<Object>} templates - The list of review templates.
   * @property {Boolean} loadingTemplates - Whether the component is currently loading templates.
   * @property {Object} form - The form data for creating or editing a review cycle.
   * @property {String} settingsJson - The JSON settings for the review cycle.
   */
/* <<<<<<<<<<  019ce29b-bf7b-448c-975a-7cad99586471  >>>>>>>>>>> */
  data() {
    return {
      loading: true,
      saving: false,
      items: [],
      search: '',
      statusFilter: '',

      showModal: false,
      isEditing: false,
      editingId: null,

      // Template selection
      templates: [],
      loadingTemplates: false,

      form: this.defaultForm(),
      settingsJson: '',
    }
  },
  computed: {
    filtered() {
      const s = (this.search || '').toLowerCase()
      const list = this.items.filter(it =>
        (!this.statusFilter || (it.status || 'draft') === this.statusFilter) &&
        ((it.name || '').toLowerCase().includes(s) || (it.description || '').toLowerCase().includes(s))
      )
      return list
    }
  },
  methods: {
    defaultForm() {
      return {
        name: '',
        description: '',
        start_date: moment().format('YYYY-MM-DD'),
        end_date: moment().add(1, 'month').format('YYYY-MM-DD'),
        self_review_deadline: '',
        manager_review_deadline: '',
        status: 'draft',
        review_template_id: '',
        settings: {},
      }
    },
    formatDate(val) {
      if (!val) return '-'
      return moment(val).format('MMM DD, YYYY')
    },
    statusBadge(status) {
      const st = status || 'draft'
      const map = { draft: 'badge bg-secondary', active: 'badge bg-success', closed: 'badge bg-warning', archived: 'badge bg-dark' }
      return map[st] || 'badge bg-secondary'
    },
    async refresh() {
      try {
        this.loading = true
        const res = await reviewCycleService.list(this.statusFilter ? { status: this.statusFilter } : {})
        const payload = res?.data || res || []
        this.items = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : [])
      } catch (e) {
        notify.alert?.error?.('Failed to fetch review cycles')
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.isEditing = false
      this.editingId = null
      this.form = this.defaultForm()
      this.settingsJson = ''
      this.showModal = true
    },
    async fetchTemplates() {
      try {
        this.loadingTemplates = true
        const response = await reviewTemplateService.listTemplates()
        this.templates = Array.isArray(response?.data) ? response.data : (response || [])
      } catch (error) {
        console.error('Failed to fetch templates:', error)
        notify.alert.error('Failed to load review templates')
      } finally {
        this.loadingTemplates = false
      }
    },
    openEdit(row) {
      this.isEditing = true
      this.editingId = row.id
      this.form = {
        name: row.name || '',
        description: row.description || '',
        start_date: (row.start_date || '').slice(0,10),
        end_date: (row.end_date || '').slice(0,10),
        self_review_deadline: (row.self_review_deadline || '').slice(0,10),
        manager_review_deadline: (row.manager_review_deadline || '').slice(0,10),
        status: row.status || 'draft',
        review_template_id: row.review_template_id || '',
        settings: row.settings || {},
      }
      this.settingsJson = row.settings ? JSON.stringify(row.settings, null, 2) : ''
      this.showModal = true
    },
    closeModal() {
      if (!this.saving) this.showModal = false
    },
    parseSettings() {
      if (!this.settingsJson || !this.settingsJson.trim()) return null
      try {
        const parsed = JSON.parse(this.settingsJson)
        if (typeof parsed === 'object') return parsed
        notify.alert?.error?.('Settings must be a JSON object')
        return null
      } catch (e) {
        notify.alert?.error?.('Invalid JSON in settings')
        return null
      }
    },
    async save() {
      const settings = this.parseSettings()
      if (this.settingsJson && settings === null) return
      try {
        this.saving = true
        const payload = { ...this.form, settings }
        if (!this.isEditing) await reviewCycleService.create(payload)
        else await reviewCycleService.update(this.editingId, payload)
        await this.refresh()
        this.showModal = false
      } catch (e) {
        console.error(e)
      } finally {
        this.saving = false
      }
    },
    confirmDelete(row) {
      confirmDialog.warning('Delete Review Cycle', `Are you sure you want to delete "${row.name}"?`, async () => {
        try { await reviewCycleService.destroy(row.id); await this.refresh() } catch (e) { console.error(e) }
      })
    },
    async activate(row) {
      try {
        await reviewCycleService.activate(row.id)
        await this.refresh()
      } catch (e) { console.error(e) }
    },
  },
  watch: {
    statusFilter() { this.refresh() }
  },
  async mounted() {
    await Promise.all([this.refresh(), this.fetchTemplates()])
  }
}
</script>

<style scoped>
.search-input { min-width: 260px; }
.bg-primary-light { background: rgba(101,42,163,0.08); }
</style>
